import { computed, ref } from 'vue'
import { MutationTypes, useStore } from '@/store'
import { Slide, Templates } from '@/types/slides'
import { createRandomCode } from '@/utils/common'
import { copyText, readClipboard } from '@/utils/clipboard'
import { encrypt } from '@/utils/crypto'
import { createElementIdMap } from '@/utils/element'
import { KEYS } from '@/configs/hotkey'
import { message, Modal } from 'ant-design-vue'
import usePasteTextClipboardData from '@/hooks/usePasteTextClipboardData'
import useHistorySnapshot from '@/hooks/useHistorySnapshot'

export default () => {
  const store = useStore()
  const slideIndex = computed(() => store.state.slideIndex)
  const theme = computed(() => store.state.theme)
  const slides = computed(() => store.state.slides)
  const ratio = computed(() => store.state.viewportRatio)
  const ratioW = computed(() => store.state.viewportWidths)
  const ratioH = computed(() => store.state.viewportHeights)
  const ratioT = computed(() => store.state.viewportType)
  const currentSlide = computed<Slide>(() => store.getters.currentSlide)

  const selectedSlidesIndex = computed(() => [...store.state.selectedSlidesIndex, slideIndex.value])
  const selectedSlides = computed(() => slides.value.filter((item, index) => selectedSlidesIndex.value.includes(index)))
  const selectedSlidesId = computed(() => selectedSlides.value.map(item => item.id))

  const { pasteTextClipboardData, addSlidesFromClipboard } = usePasteTextClipboardData()
  const { addHistorySnapshot } = useHistorySnapshot()
  // 重置幻灯片
  const resetSlides = () => {
    const emptySlide = {
      id: createRandomCode(8),
      elements: [],
      background: {
        type: 'solid',
        color: theme.value.backgroundColor,
      },
      ratio: ratio,
      width: ratioW,
      height: ratioH,
      unit: ratioT
    }
    store.commit(MutationTypes.UPDATE_SLIDE_INDEX, 0)
    store.commit(MutationTypes.SET_ACTIVE_ELEMENT_ID_LIST, [])
    store.commit(MutationTypes.SET_SLIDES, [emptySlide])
  }

  /**
   * 移动页面焦点
   * @param command 移动页面焦点命令：上移、下移
   */
  const updateSlideIndex = (command: string) => {
    if (command === KEYS.UP && slideIndex.value > 0) {
      store.commit(MutationTypes.UPDATE_SLIDE_INDEX, slideIndex.value - 1)
    }
    else if (command === KEYS.DOWN && slideIndex.value < slides.value.length - 1) {
      store.commit(MutationTypes.UPDATE_SLIDE_INDEX, slideIndex.value + 1)
    }
  }

  // 将当前页面数据加密后复制到剪贴板
  const copySlide = () => {
    if (slides.value.length >= 100) {
      message.error('每个画布只能创建100页！')
      return
    }
    const text = encrypt(JSON.stringify({
      type: 'slides',
      data: selectedSlides.value,
    }))

    copyText(text).then(() => {
      store.commit(MutationTypes.SET_THUMBNAILS_FOCUS, true)
    })
  }

  // 尝试将剪贴板页面数据解密后添加到下一页（粘贴）
  const pasteSlide = () => {
    if (slides.value.length >= 100) {
      message.error('每个画布只能创建100页！')
      return
    }
    readClipboard().then(text => {
      pasteTextClipboardData(text,{ onlySlide: true })
    }).catch(err => message.warning(err))
  }

  // 创建一页空白页并添加到下一页
  const createSlide = () => {
    const emptySlide = {
      id: createRandomCode(8),
      elements: [],
      background: {
        type: 'solid',
        color: theme.value.backgroundColor,
      },
      ratio: ratio,
      width: ratioW,
      height: ratioH,
      unit: ratioT
    }
    if (slides.value.length >= 100) {
      message.error('每个画布只能创建100页！')
      return
    }
    store.commit(MutationTypes.SET_ACTIVE_ELEMENT_ID_LIST, [])
    store.commit(MutationTypes.ADD_SLIDE, emptySlide)
    store.commit(MutationTypes.SET_ELE_THUMB_STATE, true)
    store.commit(MutationTypes.SET_M_ADD_SHOW, false);
    store.commit(MutationTypes.SET_M_PAGE_SHOW, true);
    
    addHistorySnapshot()
     
  }
  // 根据模板展示页面
  const showSlideByTemplate = (template: Templates) => {
    // eslint-disable-next-line no-console
    store.commit(MutationTypes.SET_LAYOUTS, template)
    store.commit(MutationTypes.SET_TEMP_STATE, false)
    store.commit(MutationTypes.SET_LAY_STATE, true)
  }
  const fontsList = computed(() => store.state.slidesFontsList);
  const currFont = ref([]);
  const currFontOld = ref([]);
  const sildeFonts = computed(() => store.state.slideFonts);
  const getFonts = () => {
    // console.log(sildeFonts.value, "sildeFonts", fontsList.value);

    sildeFonts.value.map((k: any) => {
      fontsList.value.map((f: any) => {
        if (k === f.enName) {
          currFont.value.push(f as never);
        }
        if (k === f.name) {
          currFontOld.value.push(f as never);
        }
      });
    });
    document.getElementById("font-f")?.remove();
    const style = document.createElement("style");
    style.id = "font-f";
    style.type = "text/css";
    currFont.value.forEach((f: any) => {
      style.innerText +=
        "@font-face {font-family:'" + f.enName + "';src:url('" + f.filePath + "')}";
    });
    currFontOld.value.forEach((f: any) => {
      style.innerText +=
        "@font-face {font-family:'" + f.name + "';src:url('" + f.filePath + "')}";
    });
    document.getElementsByTagName("head")[0].appendChild(style);
    store.commit(
      MutationTypes.SET_SLIDES_USE_FONTS_LIST,
      new Set(currFont.value.concat(currFontOld.value))
    );
  };
  const LOAD_FONTS = (data: any) => {
    let fontArr: any[] = [];
    const fonts: any[] = [];
    data.forEach((item: any) => {
      item.elements.forEach((ele: any) => {
        if (ele.type === "text") {
          fontArr.push(
            String(
              ...new Set(
                ele.content && ele.content.match(/(font-family:[^><;"]*(;)?)/gi)
              )
            )
          );
          if (ele.defaultFontName) {
            fontArr.push("font-family: " + ele.defaultFontName);
          }
        } else if (ele.type === "rect") {
          fontArr.push(
            String(
              ...new Set(
                ele.text && ele.text.content.match(/(font-family:[^><;"]*(;)?)/gi)
              )
            )
          );
        } else if (ele.type === "shape") {
          fontArr.push(
            String(
              ...new Set(
                ele.text && ele.text.content.match(/(font-family:[^><;"]*(;)?)/gi)
              )
            )
          );
        }
      });
    });

    fontArr = [...new Set(fontArr)];
    
    if (fontArr.length > 0) {
      fontArr.forEach((item) => {
        if (item !== "") {
          const f = String(item);
          fonts.push(f.split(":")[1].replace(/ /g, ""));
        }
      });
    }
    store.commit(MutationTypes.SET_SLIDES_FONTS, fonts);
    getFonts()
  };
  // 根据模板创建新页面
  const createSlideByTemplate = (slide: Slide) => {
    const cm = ref(28.333)
    const mm = ref(2.833)
    const viewportWidths = ref()
    const viewportHeights = ref()
    const { groupIdMap, elIdMap } = createElementIdMap(slide.elements)
    for (const element of slide.elements) {
      element.id = elIdMap[element.id]
      if (element.groupId) element.groupId = groupIdMap[element.groupId]
    }
    
    const newSlide = {
      ...slide,
      id: createRandomCode(8),
    }
    if (newSlide.unit === 'cm') {
      viewportWidths.value = Number((newSlide.width * cm.value).toFixed(2))
      viewportHeights.value = Number((newSlide.height * cm.value).toFixed(2))
    }
    else if (newSlide.unit === 'mm') {
      viewportWidths.value = Number((newSlide.width * mm.value).toFixed(2))
      viewportHeights.value = Number((newSlide.height * mm.value).toFixed(2))
    }
    else {
      viewportWidths.value = newSlide.width
      viewportHeights.value = newSlide.height
    }
    // Modal.confirm({
    //   title: '替换当前页面？',
    //   content: '使用模板后，当前页面的内容将会被替换',
    //   okText:'替换页面',
    //   cancelText:'添加到新页面',
    //   closable: true,
    //   onOk() {
    //     return new Promise((resolve, reject) => {
    //       confirmOnOk(resolve, reject)
    //     })
    //     .catch(() => 
    //     console.log('Oops errors!')
    //     ) 
    //   },
    //   onCancel() {
    //     return new Promise((resolve, reject) => {
    //       confirmOnCancel(resolve, reject)
    //     })
    //     .catch(() => 
    //     console.log('Oops errors!')
    //     ) 
    //   },
    // })
    if (slides.value[selectedSlidesIndex.value as any].elements.length <= 0) {
      Modal.confirm({
        title: '替换当前页面？',
        content: '使用模板后，当前页面的内容将会被替换',
        okText:'替换页面',
        cancelText:'取消',
        onOk() {
          return new Promise((resolve, reject) => {
            confirmOnOk(resolve, reject)
          })
          // .catch(() => 
          // console.log('Oops errors!')
          // ) 
        }
      })
    }
    else {
      Modal.confirm({
        maskClosable:true,
        title: '将模板添加为新页面',
        content: '使用模板后，模板内容将应用至新页面',
        okText:'添加为新页面',
        cancelText:'取消',
        onOk() {
          return new Promise((resolve, reject) => {
            confirmOnCancel(resolve, reject)
          })
          // .catch(() => 
          // console.log('Oops errors!')
          // ) 
        }
      })
    }

    const confirmOnOk = (resolve:any, reject:any) => {
      if (slides.value.length >= 100) {
        message.error('每个画布只能创建100页！')
        return
      }
      store.commit(MutationTypes.SET_ACTIVE_ELEMENT_ID_LIST, [])
      store.commit(MutationTypes.UPDATE_SLIDE, newSlide)
      store.commit(MutationTypes.SET_VIEWPORT_RATIO, newSlide.ratio)
      store.commit(MutationTypes.SET_VIEWPORT_RATIO_WIDTHS, newSlide.width)
      store.commit(MutationTypes.SET_VIEWPORT_RATIO_HEIGHTS, newSlide.height)
      store.commit(MutationTypes.SET_VIEWPORT_RATIO_TYPE, newSlide.unit)
      store.commit(MutationTypes.SET_VIEWPORT_RATIO_WIDTH, viewportWidths.value)
      store.commit(MutationTypes.SET_VIEWPORT_RATIO_HEIGHT, viewportHeights.value)
      store.commit(MutationTypes.SET_M_TEMPLATE_SHOW, false);
      store.commit(MutationTypes.SET_TEMP_MODAL_STATE, false)
      LOAD_FONTS(slides.value)
      resolve()
      addHistorySnapshot()
    }
    const confirmOnCancel = (resolve:any, reject:any) => {
      if (slides.value.length >= 100) {
        message.error('每个画布只能创建100页！')
        return
      }
      store.commit(MutationTypes.SET_ACTIVE_ELEMENT_ID_LIST, [])
      store.commit(MutationTypes.ADD_SLIDE, newSlide)
      store.commit(MutationTypes.SET_VIEWPORT_RATIO, newSlide.ratio)
      store.commit(MutationTypes.SET_VIEWPORT_RATIO_WIDTHS, newSlide.width)
      store.commit(MutationTypes.SET_VIEWPORT_RATIO_HEIGHTS, newSlide.height)
      store.commit(MutationTypes.SET_VIEWPORT_RATIO_TYPE, newSlide.unit)
      store.commit(MutationTypes.SET_VIEWPORT_RATIO_WIDTH, viewportWidths.value)
      store.commit(MutationTypes.SET_VIEWPORT_RATIO_HEIGHT, viewportHeights.value)
      store.commit(MutationTypes.SET_M_TEMPLATE_SHOW, false);
      store.commit(MutationTypes.SET_TEMP_MODAL_STATE, false)
      LOAD_FONTS(slides.value)
      resolve()
      addHistorySnapshot()
    }
  }
  // 应用整套模板
  const createSlideTemplate = (slide:any) => {
    
    Modal.confirm({
      title: '应用整套模板？',
      content: '使用模板后，当前页面的内容将会被替换',
      okText:'应用模板',
      cancelText:'取消',
      onOk() {
        return new Promise((resolve, reject) => {
          confirmOnOk(resolve, reject)
        })
        // .catch(() => 
        // console.log('Oops errors!')
        // ) 
      },
    })
    const confirmOnOk = (resolve:any, reject:any) => {
      if (slides.value.length >= 100) {
        message.error('每个画布只能创建100页！')
        return
      }

      store.commit(MutationTypes.UPDATE_SLIDE_INDEX, 0)
      store.commit(MutationTypes.SET_ACTIVE_ELEMENT_ID_LIST, [])
      store.commit(MutationTypes.SET_TEMP_MODAL_STATE, false)
      store.commit(MutationTypes.SET_SLIDES, slide)
      store.commit(MutationTypes.SET_M_TEMPLATE_SHOW, false);
      LOAD_FONTS(slides.value)
      resolve()
      addHistorySnapshot()
       
    }
  }
  // 将当前页复制一份到下一页
  const copyAndPasteSlide = () => {
    if (slides.value.length >= 100) {
      message.error('每个画布只能创建100页！')
      return
    }
    const slide = JSON.parse(JSON.stringify(currentSlide.value))
    addSlidesFromClipboard([slide])
  }

  // 删除当前页，若将删除全部页面，则执行重置幻灯片操作
  const deleteSlide = (targetSlidesId = selectedSlidesId.value) => {
    if (slides.value.length === targetSlidesId.length) resetSlides()
    else store.commit(MutationTypes.DELETE_SLIDE, targetSlidesId)

    store.commit(MutationTypes.UPDATE_SELECTED_SLIDES_INDEX, [])

    addHistorySnapshot()
     
  }

  // 将当前页复制后删除（剪切）
  // 由于复制操作会导致多选状态消失，所以需要提前将需要删除的页面ID进行缓存
  const cutSlide = () => {
    const targetSlidesId = [...selectedSlidesId.value]
    copySlide()
    deleteSlide(targetSlidesId)
  }

  // 选中全部幻灯片
  const selectAllSlide = () => {
    const newSelectedSlidesIndex = Array.from(Array(slides.value.length), (item, index) => index)
    store.commit(MutationTypes.SET_ACTIVE_ELEMENT_ID_LIST, [])
    store.commit(MutationTypes.UPDATE_SELECTED_SLIDES_INDEX, newSelectedSlidesIndex)
  }

  // 设置展示所有模板
  const showAllTemplate = () => {
    store.commit(MutationTypes.SET_TEMP_STATE, true)
    store.commit(MutationTypes.SET_LAY_STATE, false)
  }

  // 设置模板标题
  const setSlideTitle = () => {
    // eslint-disable-next-line no-console
    // console.log('ddd')
  }
  // 拖拽调整幻灯片顺序同步数据
  const sortSlides = (newIndex: number, oldIndex: number) => {
    if (oldIndex === newIndex) return
  
    const _slides = JSON.parse(JSON.stringify(slides.value))
    const _slide = _slides[oldIndex]
    _slides.splice(oldIndex, 1)
    _slides.splice(newIndex, 0, _slide)
    store.commit(MutationTypes.SET_SLIDES, _slides)
    store.commit(MutationTypes.UPDATE_SLIDE_INDEX, newIndex)
  }
  return {
    resetSlides,
    updateSlideIndex,
    copySlide,
    pasteSlide,
    createSlide,
    createSlideByTemplate,
    copyAndPasteSlide,
    deleteSlide,
    cutSlide,
    selectAllSlide,
    showSlideByTemplate,
    showAllTemplate,
    setSlideTitle,
    createSlideTemplate,
    sortSlides
  }
}